import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft } from 'react-icons/fa';
import 'react-toastify/dist/ReactToastify.css';

import { SEC_ApiCall, SEC_DataApiPost } from '../func.js';
import { userAppData } from '../MainApp.js';
import Header from '../common/header.js';

import './SysManagerNewLearnerDesktop.css';
import './SysManagerNewLearnerMobile.css';

const toastConfig = { autoClose: 4000, position: 'top-right' };

export default function SysManagerNewLearner() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [admissionNumber, setAdmissionNumber] = useState('');
  const [pupilName, setPupilName] = useState('');
  const [upn, setUpn] = useState('');
  const [isFsm, setIsFsm] = useState(false);
  const [isMtt, setIsMtt] = useState(false);
  const [isAln, setIsAln] = useState(false);
  const [selectedGroupName, setSelectedGroupName] = useState('');
  const [selectedClassName, setSelectedClassName] = useState('');

  const sysManagerPerms = userAppData?.sysManager;

  useEffect(() => {
    if (!sysManagerPerms || sysManagerPerms.perms !== 1 || !sysManagerPerms.learnerAdmin) {
      navigate('/app');
      return;
    }

    const loadGroups = async () => {
      setIsLoadingGroups(true);
      try {
        const response = await SEC_ApiCall('sysmanager/learners/getGroups');
        const responseGroups = Array.isArray(response?.groups) ? response.groups : [];
        setGroups(responseGroups);
      } catch (error) {
        console.error('Failed to load learner groups', error);
        toast.error('\u274c Failed to load learner groups. Please refresh.', toastConfig);
      } finally {
        setIsLoadingGroups(false);
      }
    };

    loadGroups();
  }, [navigate, sysManagerPerms]);

  const selectedGroup = useMemo(() => {
    return groups.find(group => group.groupName === selectedGroupName) || null;
  }, [groups, selectedGroupName]);

  const classOptions = useMemo(() => {
    if (!selectedGroup || !Array.isArray(selectedGroup.classes)) {
      return [];
    }
    return selectedGroup.classes;
  }, [selectedGroup]);

  const handleGroupChange = (event) => {
    const nextGroup = event.target.value;
    setSelectedGroupName(nextGroup);
    setSelectedClassName('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedAdmission = admissionNumber.trim();
    const trimmedName = pupilName.trim();
    const trimmedUpn = upn.trim();

    if (!trimmedAdmission || !trimmedName || !selectedGroupName || !selectedClassName) {
      toast.error('\u274c Please complete all required fields.', toastConfig);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        id: trimmedAdmission,
        pupilName: trimmedName,
        UPN: trimmedUpn,
        FSM: isFsm,
        MTT: isMtt,
        ALN: isAln,
        classId: selectedClassName,
        groupId: selectedGroupName,
      };

      const response = await SEC_DataApiPost('sysmanager/learners/new', JSON.stringify(payload));

      if (response?.created) {
        toast.success(`\u2705 Learner created: ${trimmedName}`, toastConfig);
        setAdmissionNumber('');
        setPupilName('');
        setUpn('');
        setIsFsm(false);
        setIsMtt(false);
        setIsAln(false);
        setSelectedGroupName('');
        setSelectedClassName('');
      } else {
        const errorMessage = response?.error || 'Unable to create learner. Please try again.';
        toast.error(`\u274c ${errorMessage}`, toastConfig);
      }
    } catch (error) {
      console.error('Create learner request failed', error);
      toast.error('\u274c Unable to create learner. Please try again.', toastConfig);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='main_div'>
      <div className='internal_main_div sysManager_newLearner_container'>
        <Header
          title='Add New Learner'
          subtitle='Create a new learner record for this school.'
          rightContent={(
            <button className='module-page-header__button' type='button' onClick={() => navigate('/app/sysmanager/learners')}>
              <FaArrowLeft aria-hidden='true' />
              Back to Learners
            </button>
          )}
        />

        <form className='sysManager_newLearner_form' onSubmit={handleSubmit}>
          <div className='sysManager_newLearner_field'>
            <label htmlFor='admissionNumber'>Admission Number</label>
            <input
              id='admissionNumber'
              type='text'
              value={admissionNumber}
              onChange={(event) => setAdmissionNumber(event.target.value)}
              placeholder='e.g. 5021'
              required
            />
          </div>

          <div className='sysManager_newLearner_field'>
            <label htmlFor='pupilName'>Learner Name</label>
            <input
              id='pupilName'
              type='text'
              value={pupilName}
              onChange={(event) => setPupilName(event.target.value)}
              placeholder='e.g. Sophie-May Hughes'
              required
            />
          </div>

          <div className='sysManager_newLearner_field'>
            <label htmlFor='upn'>UPN</label>
            <input
              id='upn'
              type='text'
              value={upn}
              onChange={(event) => setUpn(event.target.value)}
              placeholder='e.g. Y664226714001'
            />
          </div>

          <div className='sysManager_newLearner_field'>
            <label htmlFor='groupSelect'>Group</label>
            <select
              id='groupSelect'
              value={selectedGroupName}
              onChange={handleGroupChange}
              disabled={isLoadingGroups}
              required
            >
              <option value=''>Select a group</option>
              {groups.map((group) => (
                <option key={group.groupName} value={group.groupName}>
                  {group.groupName}
                </option>
              ))}
            </select>
            {isLoadingGroups && <span className='sysManager_newLearner_hint'>Loading groups...</span>}
          </div>

          <div className='sysManager_newLearner_field'>
            <label htmlFor='classSelect'>Class</label>
            <select
              id='classSelect'
              value={selectedClassName}
              onChange={(event) => setSelectedClassName(event.target.value)}
              disabled={!selectedGroupName}
              required
            >
              <option value=''>Select a class</option>
              {classOptions.map((className) => (
                <option key={className} value={className}>
                  {className}
                </option>
              ))}
            </select>
          </div>

          <div
            className='sysManager_newLearner_flags'
            role='group'
            aria-labelledby='learnerAttributesTitle'
          >
            <span id='learnerAttributesTitle' className='sysManager_newLearner_flagsTitle'>
              Learner Attributes
            </span>
            <div className='sysManager_newLearner_flagGrid'>
              <label className='sysManager_newLearner_checkboxRow'>
                <input
                  type='checkbox'
                  checked={isFsm}
                  onChange={(event) => setIsFsm(event.target.checked)}
                />
                <span>FSM</span>
              </label>
              <label className='sysManager_newLearner_checkboxRow'>
                <input
                  type='checkbox'
                  checked={isMtt}
                  onChange={(event) => setIsMtt(event.target.checked)}
                />
                <span>MTT</span>
              </label>
              <label className='sysManager_newLearner_checkboxRow'>
                <input
                  type='checkbox'
                  checked={isAln}
                  onChange={(event) => setIsAln(event.target.checked)}
                />
                <span>ALN</span>
              </label>
            </div>
          </div>

          <div className='sysManager_newLearner_actions'>
            <button
              className='sysManager_newLearner_primaryButton'
              type='submit'
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving learner...' : 'Create learner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
